import React from 'react';
import { TerminalState } from './types';
import '@xterm/xterm/css/xterm.css';

interface TerminalCoreProps {
  terminalRef: (node: HTMLDivElement | null) => void;
  state: TerminalState;
  className?: string;
}

/**
 * Core terminal display component - handles only rendering
 */
export const TerminalCore: React.FC<TerminalCoreProps> = ({ 
  terminalRef, 
  state,
  className = ''
}) => {
  // Render terminal with error overlay if needed
  const showError = state.type === 'error';
  
  // For error state, still render the terminal but with an overlay
  if (showError) {
    return (
      <div className={`relative h-full w-full ${className}`}>
        {/* Still render the terminal div so content is preserved */}
        <div 
          ref={terminalRef}
          className="h-full w-full"
          style={{ 
            backgroundColor: 'transparent',
            fontSmooth: 'always',
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale'
          }}
        />
        
        {/* Error overlay - positioned at top */}
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-10 max-w-md">
          <div className="bg-red-950/90 backdrop-blur-sm border border-red-500/30 rounded-lg p-4 shadow-2xl">
            <div className="flex items-start space-x-3">
              <svg className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div className="flex-1">
                <p className="text-white font-medium text-sm">Connection Error</p>
                <p className="text-red-200 text-sm mt-1">{state.message}</p>
                {state.retryable && (
                  <p className="text-red-300 text-xs mt-2 animate-pulse">Reconnecting...</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render loading state
  if (state.type === 'initializing' || state.type === 'connecting') {
    return (
      <div className={`absolute inset-0 flex items-center justify-center bg-black ${className}`}>
        <div className="text-center">
          <div className="relative w-16 h-16 mx-auto mb-4">
            <div className="absolute inset-0 border-4 border-gray-800 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-blue-500 rounded-full animate-spin" 
              style={{ borderTopColor: 'transparent', borderRightColor: 'transparent' }}></div>
          </div>
          <p className="text-gray-400">
            {state.type === 'initializing' ? 'Initializing terminal...' : 'Connecting to instance...'}
          </p>
        </div>
      </div>
    );
  }

  // Render terminal
  return (
    <div 
      ref={terminalRef}
      className={`h-full w-full ${className}`}
      style={{ 
        backgroundColor: 'transparent',
        fontSmooth: 'always',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale'
      }}
    />
  );
};

export default TerminalCore;